<div class="tile-padding-first tile-padding admin-page">

  @if (available === false) {
    <p id="emailTemplatesUnavailable">Email templates are not switched on in this environment.</p>
  }

  @if (available) {
    @if (error) {
      <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded">{{error}}</div>
    }

    <!-- Editing: the whole page is the editor and its live preview, side by side. -->
    @if (draft && selected && wording) {
      <section id="emailWordingEditor">
        <div class="admin-toolbar admin-toolbar--center tw:sticky tw:top-0 tw:z-10 tw:bg-white tw:border-b tw:py-2 tw:mb-4">
          <h3 class="admin-h2 tw:mr-auto">Editing: {{selected.name}}
            @if (draftChanged) { <span class="tw:text-sm tw:text-gray-600" id="emailWordingUnsaved"> · unsaved changes</span> }
          </h3>
          <button type="button" class="btn btn-link" (click)="startFromDefault()">Start from the default</button>
          <button type="button" class="btn btn-secondary" id="emailWordingCancel" (click)="cancelEditing()">Cancel</button>
          <button type="button" class="btn btn-primary" id="emailWordingSave" [disabled]="savingWording || draftErrors.length > 0"
            (click)="saveWording()">{{savingWording ? 'Saving…' : 'Save wording'}}</button>
        </div>

        @if (!wording.overridesEnabled) {
          <p class="tw:text-sm" id="emailWordingSwitchOff">Email wording overrides (<code>emailtemplates.overrides</code>) is off: what you save is kept, but the default is still sent until it is switched on.</p>
        }

        @if (draftErrors.length) {
          <div class="tw:mb-3 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" id="emailWordingErrors" role="alert">
            <b>This wording cannot be saved yet:</b>
            <ul class="tw:mb-0">@for (e of draftErrors; track $index) { <li>{{e}}</li> }</ul>
          </div>
        }

        <div class="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6">
          <div>
            <label class="field__label" for="emailWordingSubject">Subject</label>
            <input id="emailWordingSubject" class="field__control" type="text" [(ngModel)]="draft.subject"
              (ngModelChange)="draftEdited()" maxlength="400" />

            <label class="field__label tw:mt-3" for="emailWordingBody" id="emailWordingBodyLabel">Body ({{wording.isHtml ? 'HTML' : 'plain text'}})</label>
            @if (richEditor) {
              <!-- emailtemplates.rich-editor: the markup editor, each option behind its own sub-switch. -->
              <app-markup-editor #bodyEditor id="emailWordingBodyEditor" labelledBy="emailWordingBodyLabel"
                [tokens]="wording.tokens" [html]="wording.isHtml" [options]="richEditor" [alsoUses]="draft.subject"
                [(ngModel)]="draft.body" (ngModelChange)="draftEdited()"></app-markup-editor>
            } @else {
              <textarea #bodyInput id="emailWordingBody" class="field__control tw:font-mono tw:text-sm" rows="22"
                spellcheck="false" [(ngModel)]="draft.body" (ngModelChange)="draftEdited()"></textarea>
            }

            <h5 class="admin-h4">Placeholders</h5>
            <p class="admin-lede">Click to add at the cursor in the body. Each is filled with the email's own value when it is sent. <b>*</b> must stay in the wording.</p>
            <dl class="admin-dl tw:text-sm" id="emailWordingTokens">
              @for (token of wording.tokens; track token.name) {
                <dt>
                  <button type="button" class="btn btn-secondary btn-sm tw:font-mono" [attr.data-token]="token.name"
                    [title]="token.description" (click)="insertTokenAtCursor(token)">{{placeholder(token)}}@if (token.required) {<b> *</b>}</button>
                </dt>
                <dd class="tw:self-center">{{token.description}}</dd>
              }
            </dl>
          </div>

          <div>
            <div class="admin-toolbar admin-toolbar--center tw:mb-2">
              <h4 class="admin-h3 tw:mr-auto">Preview <span class="tw:text-sm tw:font-normal tw:text-gray-600" id="emailTemplateDraftPreview">· draft, not saved, sample data</span></h4>
              @if (brands.length > 1) {
                <select id="emailWordingBrand" class="field__control tw:w-auto" aria-label="Brand" [(ngModel)]="brandId" (ngModelChange)="brandChanged()">
                  @for (brand of brands; track brand.applicationId) {
                    <option [ngValue]="brand.applicationId">{{brand.name}}</option>
                  }
                </select>
              }
            </div>
            <ng-container *ngTemplateOutlet="previewBlock"></ng-container>
          </div>
        </div>
      </section>
    } @else {

      @if (environmentAddresses) {
        <details class="tw:mb-4" id="emailEnvironmentAddresses">
          <summary><b>Where email goes in this environment</b>
            @if (environmentAddresses.emailOverride) { (redirected to {{environmentAddresses.emailOverride}}) }
          </summary>
          <dl class="admin-dl tw:mt-2">
            <dt>Redirect (EmailOverride)</dt>
            <dd>{{environmentAddresses.emailOverride || 'None: email goes to its real recipients'}}</dd>
            <dt>Receives own tests directly</dt>
            <dd>{{environmentAddresses.testOverrideBypass.length ? environmentAddresses.testOverrideBypass.join(', ') : 'Nobody'}}</dd>
            <dt>From, with no brand</dt><dd>{{environmentAddresses.defaultFrom || 'Not configured: such emails are not sent'}}</dd>
            @for (brand of environmentAddresses.brands; track brand.applicationId) {
              <dt>From, {{brand.name}}</dt><dd>{{brand.from || 'Not configured'}}</dd>
            }
          </dl>
          @if (presetSettings) {
          <p class="tw:text-sm tw:mt-2">
            Every preset address is a setting.
            <a href="/service-processes?tab=settings" id="emailPresetSettingsLink" (click)="openSettings($event)">Open System Controls → Settings</a>
            to see them all{{canEdit ? '' : ' (editing needs access to System Controls)'}}.
          </p>
          }
        </details>
      }

      <div class="tw:flex tw:flex-wrap tw:gap-6">

        <aside class="tw:w-full tw:lg:w-1/4" id="emailTemplateList">
          <label class="field__label" for="emailTemplateSearch">Search</label>
          <input id="emailTemplateSearch" class="field__control" type="text" [(ngModel)]="search"
            (ngModelChange)="applySearch()" placeholder="Name, trigger, recipient or process key" />

          @if (loading) {
            <p>Loading…</p>
          }
          @for (group of groups; track group.category) {
            <h5 class="admin-h4">{{group.category}}</h5>
            <ul class="admin-nav-list">
              @for (template of group.templates; track template.key) {
                <li>
                  <button type="button" class="btn btn-link"
                    [class.tw:font-bold]="selected?.key === template.key"
                    [attr.data-template]="template.key"
                    (click)="select(template)">{{template.name}}</button>
                  @if (template.hasSavedWording) {
                    <span class="tw:text-xs tw:text-gray-600" data-saved-wording> · edited</span>
                  }
                </li>
              }
            </ul>
          }
        </aside>

        @if (selected) {
          <section class="tw:flex-1 tw:min-w-0" id="emailTemplateDetail">
            <div class="admin-toolbar tw:mb-3">
              <div class="tw:mr-auto tw:min-w-0">
                <h3 class="admin-h2">{{selected.name}}</h3>
                @if (wording) {
                  <span class="tw:text-sm" id="emailWordingStatus"
                    [class.tw:text-red-600]="wording.savedErrors.length > 0">{{wordingStatus}}</span>
                }
              </div>
              @if (brands.length > 1) {
                <div>
                  <label class="field__label" for="emailTemplateBrand">Brand</label>
                  <select id="emailTemplateBrand" class="field__control" [(ngModel)]="brandId" (ngModelChange)="brandChanged()">
                    @for (brand of brands; track brand.applicationId) {
                      <option [ngValue]="brand.applicationId">{{brand.name}}</option>
                    }
                  </select>
                </div>
              }
              @if (wording && canChangeWording) {
                <button type="button" class="btn btn-secondary" id="emailWordingEdit" (click)="edit()">Edit wording</button>
              }
              @if (canChangeWording) {
                <button type="button" class="btn btn-primary" id="emailTemplateSendTest"
                  [disabled]="sending" (click)="sendTest()">{{sending ? 'Sending…' : 'Send test to me'}}</button>
              }
            </div>

            @if (wordingMessage) {
              <p role="status" class="tw:text-green-700" id="emailWordingMessage">{{wordingMessage}}</p>
            }
            @if (restoreErrors.length) {
              <div class="tw:mb-3 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" id="emailWordingRestoreErrors" role="alert">
                <b>This version cannot be restored:</b>
                <ul class="tw:mb-0">@for (e of restoreErrors; track $index) { <li>{{e}}</li> }</ul>
              </div>
            }
            @if (wording?.savedErrors?.length) {
              <div class="tw:mb-3 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" id="emailWordingSavedErrors" role="alert">
                The saved wording no longer fits this email, so while overrides are on the email is <b>not sent</b>. Edit or reset it:
                <ul class="tw:mb-0">@for (e of wording.savedErrors; track $index) { <li>{{e}}</li> }</ul>
              </div>
            }
            @if (testResult) {
              <div id="emailTemplateTestResult" role="status" class="tw:mb-3"
                [class.tw:text-green-700]="testResult.sent" [class.tw:text-red-600]="!testResult.sent">
                @if (testResult.sent) {
                  Test sent to {{testResult.addressedTo}}.
                  @if (testResult.redirectedTo) {
                    This environment redirects all email, so it was delivered to {{testResult.redirectedTo}}.
                  }
                } @else {
                  Not sent: {{testResult.reason}}
                }
              </div>
            }

            <div class="tw:grid tw:grid-cols-1 tw:xl:grid-cols-3 tw:gap-6">
              <div class="tw:xl:col-span-2 tw:min-w-0">
                <ng-container *ngTemplateOutlet="previewBlock"></ng-container>
              </div>

              <aside class="tw:text-sm tw:min-w-0">
                <h5 class="admin-h4">About this email</h5>
                <dl>
                  <dt>Sent when</dt><dd>{{selected.trigger}}</dd>
                  <dt>Sent to</dt><dd><admin-email [value]="selected.recipients" /></dd>
                  <dt>Process switch</dt><dd><code>{{selected.processKey}}</code> (and All outbound email)</dd>
                  @for (preset of selected.presetAddresses; track preset.label) {
                    <ng-container *ngTemplateOutlet="presetAddress; context: { $implicit: preset }"></ng-container>
                  }
                  @if (selected.notes) {
                    <dt>Notes</dt><dd>{{selected.notes}}</dd>
                  }
                  <dt>Built in</dt><dd><code>{{selected.sourceFile}}</code></dd>
                </dl>

                @if (wording) {
                  <h5 class="admin-h4">Wording</h5>
                  @if (wording.saved) {
                    <p class="tw:mb-1">Saved by <admin-email [value]="wording.saved.updatedBy || 'unknown'" />, {{wording.saved.updatedUtc | date:'d MMM yyyy HH:mm'}}.</p>
                    @if (canChangeWording) {
                      <button type="button" class="btn btn-link tw:p-0" id="emailWordingReset" [disabled]="savingWording" (click)="resetWording()">Reset to default</button>
                    }
                  } @else {
                    <p class="tw:mb-1">The default, from code.</p>
                  }
                  @if (wording.history.length) {
                    <details class="tw:mt-2">
                      <summary>History ({{wording.history.length}})</summary>
                      <ul class="tw:pl-4">
                        @for (h of wording.history; track $index; let first = $first) {
                          <li data-history-entry>{{h.changedUtc | date:'d MMM yyyy HH:mm'}}: {{historyLabel(h)}} by <admin-email [value]="h.changedBy || 'unknown'" />
                            @if (h.subject) { (<i>{{h.subject}}</i>) }
                            <!-- emailtemplates.history-restore. The newest entry is the wording now current. -->
                            @if (historyRestore && canChangeWording && !first) {
                              <button type="button" class="btn btn-link btn-sm tw:p-0" data-history-restore
                                [disabled]="savingWording" (click)="restoreVersion(h)">Restore this version</button>
                            }
                          </li>
                        }
                      </ul>
                    </details>
                  }
                }
              </aside>
            </div>
          </section>
        }
      </div>
    }
  }
</div>

<ng-template #previewBlock>
  @if (previewLoading && !preview) {
    <p>Rendering preview…</p>
  }
  @if (preview) {
    <div id="emailTemplatePreview" class="tw:border tw:bg-white" [class.tw:opacity-60]="previewLoading">
      <div class="tw:border-b tw:p-3 tw:text-sm tw:bg-gray-50">
        <div><b>Subject:</b> {{preview.subject}}</div>
        <div><b>From:</b> {{preview.from}}</div>
        <div class="tw:text-gray-600">Sample data. SendGrid wraps this body in the brand's template <code>{{preview.sendGridTemplateId}}</code>, not shown here.</div>
      </div>
      @if (preview.isHtml) {
        <iframe title="Email preview" sandbox="" class="tw:w-full tw:block tw:border-0"
          style="min-height: 560px;" [srcdoc]="previewHtml"></iframe>
      } @else {
        <pre class="tw:whitespace-pre-wrap tw:p-3 tw:m-0">{{preview.body}}</pre>
      }
    </div>
  }
</ng-template>

<ng-template #presetAddress let-preset>
  <dt>{{preset.label}}</dt>
  <!-- emailtemplates.preset-addresses off: read-only, label and value only. -->
  @if (!presetSettings) {
  <dd data-preset-address data-preset-readonly [attr.data-setting-key]="preset.settingKey">
    @for (value of preset.values; track $index) {
      <div>
        @if (value.brand) { {{value.brand}}: }
        @if (shown(value.address, value.notUsed); as text) { <code>{{text}}</code> } @else { <i>not configured</i> }
      </div>
    }
  </dd>
  } @else {
  <dd data-preset-address [attr.data-setting-key]="preset.settingKey">
    @if (preset.perBrand) {
      <div data-preset-value="all">
        <span>Every brand without its own value: </span>
        <ng-container *ngTemplateOutlet="addressValue; context: { preset: preset, value: null, address: preset.effectiveValue, notUsed: preset.notUsed, source: preset.source }"></ng-container>
      </div>
    }
    @for (value of preset.values; track $index) {
      <div [attr.data-preset-value]="value.applicationId ?? 'all'">
        @if (value.brand) { <span>{{value.brand}}: </span> }
        <ng-container *ngTemplateOutlet="addressValue; context: { preset: preset, value: preset.perBrand ? value : null, address: value.address, notUsed: value.notUsed, source: value.source, brandRow: preset.perBrand }"></ng-container>
      </div>
    }
    @if (preset.settingKey) {
      <div class="tw:text-sm tw:text-gray-600">Setting <code>{{preset.settingKey}}</code>. {{blankHint(preset)}}</div>
    } @else {
      <div class="tw:text-sm tw:text-gray-600">From {{preset.origin}}</div>
    }
  </dd>
  }
</ng-template>

<ng-template #addressValue let-preset="preset" let-value="value" let-address="address" let-notUsed="notUsed" let-source="source" let-brandRow="brandRow">
  @if (editing === editKey(preset, value)) {
    <span class="admin-inline-edit">
      <input class="field__control" type="text" data-preset-input [attr.aria-label]="preset.label"
        [ngModel]="addressDraft" (ngModelChange)="addressDraft = $event" [disabled]="savingAddress" />
      <button type="button" class="btn btn-primary btn-sm" data-preset-save [disabled]="savingAddress" (click)="saveDraft(preset, value)">Save</button>
      @if (preset.notUsedValue) {
        <button type="button" class="btn btn-secondary btn-sm" data-preset-not-used [disabled]="savingAddress" (click)="saveNotUsed(preset, value)">Not used</button>
      }
      <button type="button" class="btn btn-link btn-sm" data-preset-cancel (click)="cancelEdit()">Cancel</button>
    </span>
    @if (addressError) {
      <div class="tw:text-red-600" role="alert" data-preset-error>{{addressError}}</div>
    }
  } @else {
    @if (shown(address, notUsed); as text) {
      <code [class.tw:italic]="notUsed">{{text}}</code>
    } @else if (source === 'environment config') {
      <i>each brand's address configured for this environment</i>
    } @else {
      <i>not configured</i>
    }
    <span class="tw:text-sm tw:text-gray-600" data-preset-source> ({{source}})</span>
    @if (canEdit && preset.settingKey && (!brandRow || canEditBrand(preset, value))) {
      <button type="button" class="btn btn-link btn-sm" data-preset-edit (click)="startEdit(preset, value)">Edit</button>
    }
  }
</ng-template>
